import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { mapApiError } from "@/lib/errors";
import { Loader2 } from "lucide-react";

interface OrgOption {
  id: string;
  name: string;
  slug: string;
}

type VerifyState = "ready" | "verifying" | "success" | "error" | "org-select";

export function VerifyPage() {
  const { t } = useTranslation();
  const { user, refreshAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") ?? "";
  const code = searchParams.get("code") ?? "";
  const [state, setState] = useState<VerifyState>(
    email && code ? "ready" : "error",
  );
  const [error, setError] = useState(
    email && code ? "" : t("verify.invalidLink"),
  );
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [loading, setLoading] = useState(false);

  async function verify() {
    setState("verifying");
    setError("");
    try {
      const res = await api<{ requires_org_selection?: boolean; orgs?: OrgOption[] }>(
        "/api/v2/auth/verify",
        { method: "POST", body: JSON.stringify({ email, code }) },
      );
      if (res.requires_org_selection && res.orgs) {
        setOrgs(res.orgs);
        setState("org-select");
      } else {
        await refreshAuth();
        setState("success");
        navigate("/", { replace: true });
      }
    } catch (err) {
      setState("error");
      setError(mapApiError(err, { 401: t("verify.linkExpired") }, t("verify.genericError")));
    }
  }

  async function handleOrgSelect(orgId: string) {
    setLoading(true);
    setError("");
    try {
      await api("/api/v2/auth/verify", {
        method: "POST",
        body: JSON.stringify({ email, code, org_id: orgId }),
      });
      await refreshAuth();
      navigate("/", { replace: true });
    } catch (err) {
      setState("error");
      setError(
        mapApiError(err, { 401: t("verify.linkExpiredRequestNew") }, t("verify.genericError")),
      );
    } finally {
      setLoading(false);
    }
  }

  if (user && state !== "verifying") {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">RaiSE</CardTitle>
          <CardDescription>
            {state === "org-select" ? t("verify.orgSelectTitle") : t("verify.title")}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          {state === "ready" && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                <Trans i18nKey="verify.clickToVerify" values={{ email }}>
                  Click below to verify <strong>{email}</strong> and continue.
                </Trans>
              </p>
              <Button onClick={verify} className="w-full">
                {t("verify.continueCta")}
              </Button>
            </div>
          )}
          {state === "verifying" && (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {t("verify.verifying")}
              </p>
            </div>
          )}
          {state === "org-select" && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {t("verify.orgSelectPrompt")}
              </p>
              {orgs.map((org) => (
                <Button
                  key={org.id}
                  variant="outline"
                  className="w-full justify-start"
                  disabled={loading}
                  onClick={() => handleOrgSelect(org.id)}
                >
                  {org.name}
                </Button>
              ))}
              {error && <p className="text-sm text-destructive">{error}</p>}
            </div>
          )}
          {state === "error" && (
            <div className="space-y-4">
              <p className="text-sm text-destructive">{error}</p>
              <div className="flex flex-col gap-2">
                <Button onClick={verify} variant="outline" disabled={!code}>
                  {t("verify.tryAgain")}
                </Button>
                <Link
                  to="/login"
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  {t("verify.goToLogin")}
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
